/* eslint-disable no-underscore-dangle */
const REVOLUT_SCRIPT_ID = 'revolut-checkout-sdk';

const revolutEnvironment =
  process.env.REACT_APP_REVOLUT_ENV === 'prod' ||
  process.env.REACT_APP_REVOLUT_ENV === 'production'
    ? 'prod'
    : 'sandbox';

const defaultRevolutScriptSrc =
  revolutEnvironment === 'sandbox'
    ? 'https://sandbox-merchant.revolut.com/embed.js'
    : 'https://merchant.revolut.com/embed.js';

const REVOLUT_SCRIPT_SRC =
  process.env.REACT_APP_REVOLUT_SCRIPT_SRC || defaultRevolutScriptSrc;

declare global {
  interface Window {
    RevolutCheckout?: ((
      token: string,
      mode?: 'sandbox' | 'prod'
    ) => Promise<unknown>) & {
      payments?: (config: {
        locale?: string;
        publicToken: string;
        mode?: 'sandbox' | 'prod';
      }) => Promise<unknown>;
    };
    __revolutScriptPromise?: Promise<void>;
  }
}

export const loadRevolutScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available'));
  }

  const existingScript = document.getElementById(
    REVOLUT_SCRIPT_ID
  ) as HTMLScriptElement | null;

  const expectedSrc = new URL(REVOLUT_SCRIPT_SRC, window.location.origin).href;
  const currentSrc = existingScript?.src
    ? new URL(existingScript.src, window.location.origin).href
    : '';

  if (existingScript && currentSrc !== expectedSrc) {
    existingScript.remove();
    window.RevolutCheckout = undefined;
    window.__revolutScriptPromise = undefined;
  }

  if (window.RevolutCheckout) {
    return Promise.resolve();
  }

  if (window.__revolutScriptPromise) {
    return window.__revolutScriptPromise;
  }

  if (existingScript && currentSrc === expectedSrc) {
    window.__revolutScriptPromise = new Promise((resolve, reject) => {
      if (window.RevolutCheckout) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => {
        if (window.RevolutCheckout) {
          resolve();
          return;
        }

        reject(new Error('Revolut SDK loaded but API is unavailable'));
      });
      existingScript.addEventListener('error', () => {
        reject(new Error('Failed to load Revolut SDK'));
      });
    });

    return window.__revolutScriptPromise;
  }

  window.__revolutScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = REVOLUT_SCRIPT_ID;
    script.src = REVOLUT_SCRIPT_SRC;
    script.async = true;

    script.onload = () => {
      if (window.RevolutCheckout) {
        resolve();
        return;
      }

      reject(new Error('Revolut SDK loaded but API is unavailable'));
    };

    script.onerror = () => {
      reject(new Error('Failed to load Revolut SDK'));
    };

    document.body.appendChild(script);
  });

  return window.__revolutScriptPromise;
};
